"use client";
import {
  type PricingMetadata,
  convertTranslatedWordToKey,
  replacePricingWordsWithLinks,
} from "@frontend/components/pricingExplainability/pricingExplainabilityCopy";
import {
  getPricingExplanation,
  getPricingKeysFromMetadata,
} from "@frontend/components/pricingExplainability/pricingExplainabilityCopy";
import { getOrganizationAssetsForOrg } from "@frontend/lib/actions/config-actions";
import {
  type TranslationNamespaceKeys,
  useTranslations,
} from "@frontend/lib/next-intl-typed";
import { useBoundStore } from "@frontend/lib/stores/globalStore";
import { Button } from "@frontend/shared/components/ui/button";
import {
  CustomDialogContent,
  Dialog,
} from "@frontend/shared/components/ui/dialog";
import { Drawer, DrawerContent } from "@frontend/shared/components/ui/drawer";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useLocale } from "next-intl";
import { useState } from "react";
import { useMediaQuery } from "usehooks-ts";

/**
 * This component renders pricing explainability given an HRT and given that insurance is involved
 * @param param0 HRT with at minimum hrtId and orgId
 * @returns React component
 */
export const PriceExplainabilityRenderer = ({
  hrt,
  textColorClass = "text-pcv2-text-grey",
}: {
  hrt: { hrtId: number; orgId: number };
  textColorClass?: string;
}) => {
  const {
    data: organizationAssets,
    isLoading: isLoadingAssets,
    error: assetsError,
  } = useQuery({
    queryKey: ["organizationAssets", hrt.orgId],
    queryFn: () => getOrganizationAssetsForOrg(hrt.orgId),
    enabled: !!hrt.orgId,
  });

  const highlightTextColor = organizationAssets?.secondaryColor;
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [open, setOpen] = useState(false);
  const [pricingWord, setPricingWord] = useState("");
  const pricingMap = useBoundStore.use.pricingMapEntries();
  const isPendingPricesMode = useBoundStore.use.isPendingPricesMode();
  const pricingMapEntry = pricingMap[hrt.hrtId];
  const userLocale = useLocale();
  const t = useTranslations("PricingDefinitions");
  const t_pricing = useTranslations("PricingExplanation");

  if (!pricingMapEntry) {
    return null;
  }

  const hrtMode = pricingMapEntry.mode;
  const hrtPrice = pricingMapEntry.priceData[hrtMode]?.price;
  const isHrtPriceZero = hrtPrice === 0;
  const metadata = pricingMapEntry.priceData[hrtMode]?.metadata;

  if (!metadata) {
    return null;
  }

  const keys = getPricingKeysFromMetadata(metadata as PricingMetadata);
  const explanation = getPricingExplanation(
    metadata as PricingMetadata,
    null, // prepurchase explainability: don't pass in bought treatment for overrides because it doesn't exist yet
    keys,
    isHrtPriceZero,
    t_pricing,
  );

  const onPricingLinkClick = (word: string) => {
    setPricingWord(word);
    if (!open) {
      setOpen(true);
    }
  };

  const sentenceCase = (sentence: string) => {
    return sentence
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  if (!explanation) {
    return null;
  }

  const explanationContent = replacePricingWordsWithLinks(
    explanation,
    onPricingLinkClick,
    highlightTextColor ?? undefined,
    userLocale,
  );

  if (isPendingPricesMode) {
    return null;
  }

  return (
    <div>
      <p className={`text-sm ${textColorClass}`}>{explanationContent}</p>
      {isMobile ? (
        <Drawer open={open} onOpenChange={setOpen}>
          <DrawerContent className="bg-white flex flex-col overflow-y-auto pb-8 p-0 overflow-x-hidden min-h-[50vh] rounded-t-3xl">
            <Button
              onClick={() => setOpen(false)}
              variant="ghost"
              className="absolute right-4 top-4 z-10 h-8 w-8 p-0 rounded-full hover:bg-transparent hover:text-gray-600"
              size="icon"
            >
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </Button>
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={pricingWord}
                className="p-10 gap-8 flex flex-col justify-between grow rounded-2xl"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                <div className="flex flex-col gap-6">
                  <h2 className="font-semibold">{sentenceCase(pricingWord)}</h2>
                  {pricingWord && (
                    <p>
                      {replacePricingWordsWithLinks(
                        t(
                          `${convertTranslatedWordToKey(pricingWord)}` as TranslationNamespaceKeys<"PricingDefinitions">,
                        ),
                        onPricingLinkClick,
                        highlightTextColor ?? undefined,
                        userLocale,
                      )}
                    </p>
                  )}
                </div>
                <Button
                  variant="outline"
                  onClick={() => setOpen(false)}
                  className="w-fit rounded-full py-6 self-end"
                  style={{
                    color: organizationAssets?.primaryColor ?? "#000000",
                    borderColor: organizationAssets?.primaryColor ?? "#000000",
                  }}
                >
                  {t("Dismiss")}
                </Button>
              </motion.div>
            </AnimatePresence>
          </DrawerContent>
        </Drawer>
      ) : (
        <Dialog open={open} onOpenChange={setOpen}>
          <CustomDialogContent className="bg-white flex flex-col overflow-y-auto pb-8 p-0 overflow-hidden max-w-lg max-h-none sm:rounded-3xl">
            <Button
              onClick={() => setOpen(false)}
              variant="ghost"
              className="absolute right-4 top-4 z-10 h-8 w-8 p-0 rounded-full hover:bg-transparent hover:text-gray-600"
              size="icon"
            >
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </Button>
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={pricingWord}
                className="p-10 gap-8 flex flex-col justify-between grow rounded-2xl"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                <div className="flex flex-col gap-6">
                  <h2 className="font-semibold text-3xl">
                    {sentenceCase(pricingWord)}
                  </h2>
                  {pricingWord && (
                    <p>
                      {replacePricingWordsWithLinks(
                        t(
                          `${convertTranslatedWordToKey(pricingWord)}` as TranslationNamespaceKeys<"PricingDefinitions">,
                        ),
                        onPricingLinkClick,
                        highlightTextColor ?? undefined,
                        userLocale,
                      )}
                    </p>
                  )}
                </div>
                <Button
                  variant="outline"
                  onClick={() => setOpen(false)}
                  className="w-fit rounded-full py-6 self-end"
                  style={{
                    color: organizationAssets?.primaryColor ?? "#000000",
                    borderColor: organizationAssets?.primaryColor ?? "#000000",
                  }}
                >
                  {t("Dismiss")}
                </Button>
              </motion.div>
            </AnimatePresence>
          </CustomDialogContent>
        </Dialog>
      )}
    </div>
  );
};
